/**
 * React Query Configuration
 * Manages data fetching, caching, and synchronization
 */

import { QueryClient, QueryKey, MutationCache, QueryCache } from '@tanstack/react-query';
import { socketService } from './socketService';
import { isRecentMutation } from './dealCache';
import { attachQueryClient, debounceQueryInvalidation } from './socketDebouncer';
import * as Sentry from '@sentry/react';
import { redactSecretPayload } from '@/utils/redactSecretPath';
import { quantJobCompletionInvalidationTargets, quantProjectCompletionInvalidationTargets } from './quantCompletionCache';
import { queryKeys } from '@/utils/queryKeys';

// ---------------------------------------------------------------------------
// Socket envelope versioning — Phase 3 tasks 3.6, 3.7, 3.8.
// ---------------------------------------------------------------------------
//
// The backend emits events in one of two shapes:
//   - Legacy (flat): `{ projectId, orgId, ... }` — no envelope wrapper.
//   - Envelope (v1/v2): `{ event, version, org_id, data: {...} }`.
//
// The `CURRENT_CLIENT_VERSION` constant defines the highest `version`
// value this client understands. An event with `version > CURRENT_CLIENT_VERSION`
// is NOT rejected — it logs a warning, soft-refreshes the affected slice,
// and moves on. This matches the forward-compat contract documented in
// `docs/socket-events.md` §1.
//
// Bump the constant in lockstep with a matching backend rollout.
export const CURRENT_CLIENT_VERSION = 2 as const;

/**
 * Normalise an incoming socket payload into the envelope shape so
 * handlers always see a consistent structure regardless of emit vintage.
 * When `payload.event` + `payload.version` are present, we treat the
 * payload as an envelope; otherwise we wrap the legacy flat shape as
 * a v1 envelope with `data === payload`.
 */
interface NormalizedSocketEnvelope {
  event: string;
  version: number;
  data: Record<string, unknown>;
  org_id?: string;
  project_id?: string;
  file_id?: string;
  deal_id?: string;
}

function normalizeSocketPayload(
  eventName: string,
  raw: unknown,
): NormalizedSocketEnvelope {
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (
      typeof obj.event === 'string' &&
      typeof obj.version === 'number' &&
      obj.data &&
      typeof obj.data === 'object'
    ) {
      return {
        event: obj.event,
        version: obj.version,
        data: obj.data as Record<string, unknown>,
        org_id: typeof obj.org_id === 'string' ? obj.org_id : undefined,
        project_id: typeof obj.project_id === 'string' ? obj.project_id : undefined,
        file_id: typeof obj.file_id === 'string' ? obj.file_id : undefined,
        deal_id: typeof obj.deal_id === 'string' ? obj.deal_id : undefined,
      };
    }
    // Legacy flat shape — wrap as v1.
    return {
      event: eventName,
      version: 1,
      data: obj,
      org_id:
        typeof obj.orgId === 'string'
          ? obj.orgId
          : typeof obj.organizationId === 'string'
            ? obj.organizationId
            : undefined,
      project_id: typeof obj.projectId === 'string' ? obj.projectId : undefined,
    };
  }
  return { event: eventName, version: 1, data: {} };
}

// Dev-only logger for real-time event debugging
const devLog = (...args: unknown[]) => console.log('[QueryClient]', ...args);

// Type for socket event data - can contain any structure
interface SocketEventData {
  [key: string]: unknown;
  projectId?: string;
  userId?: string;
  userData?: Record<string, unknown>;
  organizationId?: string;
  organizationName?: string;
  noteId?: string;
  notificationId?: string;
  organizationChanged?: boolean;
  orgId?: string;
  holdingId?: string;
  shareRequestId?: string;
  targetType?: string;
  targetId?: string;
  jobId?: string;
  fromOrganizationId?: string;
  organization?: string;
  updates?: {
    projects?: Array<{ id: string; [key: string]: unknown }>;
    organizations?: Array<{ name: string; orgId?: string; [key: string]: unknown }>;
    notifications?: unknown[];
  };
}

// Type for user data in cache
interface UserCacheData {
  uid: string;
  [key: string]: unknown;
}

// Type for notification data
interface NotificationData {
  id: string;
  read: boolean;
  [key: string]: unknown;
}

// Global mutation cache with Sentry error reporting
// Note: Individual mutation onError handlers may also capture to Sentry with more specific context.
// This global handler provides a safety net for mutations without explicit error handling.
const mutationCache = new MutationCache({
  onError: (error, variables, _context, mutation) => {
    // §4.3 (round-8d #3): a mutation KEY can embed a single-use token
    // (e.g. useOwnerInvite's ['owner','invite', <token>]). Deep-redact the key FIRST,
    // then derive EVERY downstream field (the tag, the fingerprint, and the serialised
    // context) from the redacted form — so the raw token never reaches Sentry through
    // ANY channel (a redacted context key alone would still leak via the tag/fingerprint).
    const mutationKey = mutation.options.mutationKey;
    const safeMutationKey = Array.isArray(mutationKey)
      ? (redactSecretPayload(mutationKey) as unknown[])
      : mutationKey;
    const mutationName = Array.isArray(safeMutationKey) ? safeMutationKey.join(':') : 'unknown';

    // Capture error to Sentry with mutation context
    // Using fingerprint to help Sentry group similar errors
    Sentry.captureException(error, {
      tags: {
        source: 'react-query-mutation',
        mutation: mutationName,
      },
      contexts: {
        mutation: {
          // The VARIABLES carry contact PII (requestConnect investor message,
          // updateProfile work email) — deep-redact them too before serialising.
          key: safeMutationKey ? JSON.stringify(safeMutationKey) : 'unknown',
          variables: typeof variables === 'object' && variables !== null
            ? JSON.stringify(redactSecretPayload(variables)).slice(0, 500) // Redact, then truncate large payloads
            : String(variables),
        },
      },
      fingerprint: ['react-query-mutation', mutationName, '{{ default }}'],
    });
  },
});

// Global query cache with Sentry error reporting for query failures
const queryCache = new QueryCache({
  onError: (error, query) => {
    // Only capture significant query errors (skip network errors during navigation)
    // Skip if this is a routine auth token refresh or background refetch
    const queryKey = query.queryKey;
    const queryName = Array.isArray(queryKey) ? queryKey.join(':') : 'unknown';

    // Don't capture auth-related queries (handled elsewhere). Guard runs on the RAW
    // name (read-only, nothing emitted) so 'auth'/'token' markers still short-circuit.
    if (queryName.includes('auth') || queryName.includes('token')) {
      return;
    }

    // §4.3 (round-8d #3): a query key can embed a single-use token
    // (e.g. useOwnerInvite's ['owner','invite', <token>]) — and the literal "token"
    // substring guard above does NOT catch the token VALUE (a random hex). Deep-redact
    // the key FIRST and derive the tag + context from the redacted form so the raw
    // token never reaches Sentry through the tag OR the serialised context.
    const safeQueryKey = redactSecretPayload(queryKey);
    const safeQueryName = Array.isArray(safeQueryKey) ? safeQueryKey.join(':') : queryName;

    // Capture query errors to Sentry with warning level
    // Queries can fail for expected reasons (404s, network issues, etc.)
    Sentry.withScope((scope) => {
      scope.setLevel('warning');
      scope.setTag('source', 'react-query-query');
      scope.setTag('query', safeQueryName);
      scope.setContext('query', {
        key: JSON.stringify(safeQueryKey),
        state: query.state.status,
      });
      scope.setFingerprint(['react-query-query', safeQueryName, '{{ default }}']);
      Sentry.captureException(error);
    });
  },
});

// Configure React Query with optimal defaults for our use case
export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes (increased to reduce API calls)
      staleTime: 1000 * 60 * 5,
      // Garbage collection time - cache is kept for 10 minutes (React Query v5)
      gcTime: 1000 * 60 * 10,
      // Retry failed requests only once for faster failure
      retry: 1,
      // Retry delay with exponential backoff
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus to prevent excessive API calls
      refetchOnWindowFocus: false,
      // Refetch on mount only if data is stale (FIX: was 'always', now honors staleTime)
      refetchOnMount: true as const,
      // Network mode
      networkMode: 'online',
      // Don't refetch in background
      refetchInterval: false,
      // Don't refetch on reconnect unless data is stale (FIX: was 'always', now honors staleTime)
      refetchOnReconnect: true as const
    },
    mutations: {
      // §4.3 (round-9 #10): DO NOT auto-retry mutations. A single global retry
      // replays NON-IDEMPOTENT, single-use-token POSTs one layer ABOVE
      // fetchWithActivity (whose own POST replay is already disabled). The worst
      // case: the mint-and-export operation mints up to 1000 single-use invite
      // tokens whose RAW values exist only in the first response; a dropped
      // response + auto-retry sees the tokens already active and returns an
      // `already_invited` CSV with NO recoverable links — a permanent data-loss.
      // Single-use claim/invite hooks replay the same way (a burned token +
      // duplicate pending rows). Most mutations are perfectly fine un-retried
      // (the user simply re-submits). A GENUINELY idempotent mutation (proven
      // idempotency key / safe replay semantics) can opt back in per-call via its
      // own `useMutation({ retry: 1 })` options — this default only removes the
      // UNSAFE blanket replay, it does not forbid a deliberate opt-in.
      retry: false,
      // Retry delay (only applies when a mutation explicitly opts back into retry)
      retryDelay: 1000,
      // Network mode
      networkMode: 'online'
    }
  }
});

// Phase 4 (DMS VDR): wire the singleton QueryClient into the socket
// debouncer at module load. This is the ONLY app-wide construction site
// for `queryClient`, so doing it here guarantees the wiring happens
// exactly once and BEFORE any consumer (App.tsx, hooks) imports the
// singleton. Without this, `debounceQueryInvalidation` calls fired by
// socket-event handlers would no-op silently (see socketDebouncer.ts:115).
attachQueryClient(queryClient);

// Helper function to invalidate all project-related queries
// FIX Issue 17, 18, 19: Centralized predicate-based invalidation for comprehensive cache updates
// refetchActive: when true, immediately refetch active queries (for View Projects instant updates)
const invalidateAllProjects = (queryClient: QueryClient, refetchActive = false) => {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key[0] === 'projects';
    },
    refetchType: refetchActive ? 'active' : undefined
  });
};

// Real-time cache invalidation based on WebSocket events
export const setupRealtimeInvalidation = () => {
  // Store all event handlers for cleanup
  const eventHandlers: Array<{ event: string; handler: (data: SocketEventData) => void }> = [];

  // Helper to register and track event handlers
  const registerHandler = (event: string, handler: (data: SocketEventData) => void) => {
    socketService.on(event, handler);
    eventHandlers.push({ event, handler });
  };

  // =========================================================================
  // PROJECT EVENTS
  // =========================================================================

  registerHandler('projects:status:changed', (event) => {
    if (import.meta.env.DEV) devLog('Project status changed, scoped invalidation:', event);

    // Targeted invalidation: update only the affected project and admin views
    // instead of invalidating ALL project queries (which triggers mass refetching)
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
    }
    // Admin-specific list views that need to reflect status changes
    queryClient.invalidateQueries({ queryKey: ['projects', 'pending'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'projects', 'pending'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'projects', 'all'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] });
    // Invalidate v2 project lists only for active queries (lazy refetch)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects' && key[1] === 'v2';
      },
      refetchType: 'active',
    });
  });

  registerHandler('projects:updated', (event) => {
    if (import.meta.env.DEV) devLog('Projects updated, scoped invalidation:', event);

    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
    }
    // Invalidate only active v2 project list queries (lazy refetch)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects' && key[1] === 'v2';
      },
      refetchType: 'active',
    });
  });

  // Project events
  registerHandler('project:created', (_event) => {
    if (import.meta.env.DEV) devLog('New project created, invalidating cache:', _event);
    // Invalidate all project lists to show the new project using predicate-based approach
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects';
      }
    });
    // React Query will auto-refetch active queries after invalidation
  });

  registerHandler('project:updated', (event) => {
    // CRITICAL FIX: Backend now sends event.id (not event.projectId)
    const id = event.id ?? event.projectId;
    if (import.meta.env.DEV) devLog('Project updated, invalidating cache:', id);

    if (id) {
      // Invalidate specific project query
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      // Invalidate project details (includes all sections)
      queryClient.invalidateQueries({ queryKey: ['project-details', id] });
      // Invalidate capital stack
      queryClient.invalidateQueries({ queryKey: ['project-capital-stack', id] });
    }
    // Invalidate only active v2 project list queries (lazy refetch instead of mass invalidation)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects' && key[1] === 'v2';
      },
      refetchType: 'active',
    });
  });

  registerHandler('project:deleted', (event) => {
    if (import.meta.env.DEV) devLog('Project deleted, invalidating cache:', event.projectId);
    if (event.projectId) {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['project', event.projectId] });
      queryClient.removeQueries({ queryKey: ['project-details', event.projectId] });
      queryClient.removeQueries({ queryKey: ['project-capital-stack', event.projectId] });
    }
    // Invalidate project lists using predicate-based approach
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects';
      }
    });
  });

  registerHandler('project:approved', (event) => {
    if (import.meta.env.DEV) devLog('Project approved, invalidating cache:', event.projectId);
    if (event.projectId) {
      // Invalidate specific project detail views only
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-capital-stack', event.projectId] });
    }

    // NOTE: Do NOT call invalidateAllProjects here - it causes a race condition!
    // useProjectRealtime.ts already handles approval events with direct cache updates
    // via updateProjectInAllCaches. Invalidating here triggers a refetch that can
    // overwrite the optimistic update with stale server data.

    // Invalidate pending projects for admins (both admin and legacy keys)
    queryClient.invalidateQueries({ queryKey: ['projects', 'pending'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'projects', 'pending'] });
    // Invalidate admin stats (badge counts)
    queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
  });

  registerHandler('project:rejected', (event) => {
    if (import.meta.env.DEV) devLog('Project rejected, invalidating cache:', event.projectId);
    if (event.projectId) {
      // Invalidate specific project detail views only
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-capital-stack', event.projectId] });
    }

    // NOTE: Do NOT call invalidateAllProjects here - it causes a race condition!
    // useProjectRealtime.ts already handles rejection events with direct cache updates
    // via updateProjectInAllCaches. Invalidating here triggers a refetch that can
    // overwrite the optimistic update with stale server data.

    // Invalidate pending projects for admins
    queryClient.invalidateQueries({ queryKey: ['projects', 'pending'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'projects', 'pending'] });
    // Invalidate admin stats (badge counts)
    queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
  });

  // Project moved back to pending (admin action)
  registerHandler('project:pending', (event) => {
    if (import.meta.env.DEV) devLog('Project marked pending, invalidating cache:', event.projectId);
    if (event.projectId) {
      // Invalidate specific project detail views only
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
    }

    // NOTE: Do NOT call invalidateAllProjects here - it causes a race condition!
    // useProjectRealtime.ts already handles pending events with direct cache updates
    // via updateProjectInAllCaches. Invalidating here triggers a refetch that can
    // overwrite the optimistic update with stale server data.

    queryClient.invalidateQueries({ queryKey: ['admin', 'projects', 'pending'] });
    // Invalidate admin stats (badge counts)
    queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
  });

  registerHandler('project:thumbnailUpdated', (event) => {
    if (import.meta.env.DEV) devLog('Project thumbnail updated, invalidating cache:', event.projectId);
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
    }
    // Only refetch active v2 project lists (not all project queries)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects' && key[1] === 'v2';
      },
      refetchType: 'active',
    });
    queryClient.invalidateQueries({ queryKey: ['dataroom', 'projects'] });
  });

  // H2: Centralized AI synthesis status handler (was in useProjectRealtime.ts)
  registerHandler('project:aiSynthesisStatusChanged', (event) => {
    const projectId = event?.projectId || event?.id;
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    if (projectId) {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    }
  });

  // =========================================================================
  // GEOSPATIAL EVENTS (H2 - centralized from GeospatialAnalysis.tsx)
  // =========================================================================

  registerHandler('geospatial:environmental:updated', (event) => {
    const projectId = event?.projectId;
    if (projectId) {
      queryClient.invalidateQueries({ queryKey: ['geospatial', projectId] });
    }
  });

  registerHandler('geospatial:landmarks:updated', (event) => {
    const projectId = event?.projectId;
    if (projectId) {
      queryClient.invalidateQueries({ queryKey: ['geospatial', projectId] });
    }
  });

  registerHandler('geospatial:analytics:updated', (event) => {
    const projectId = event?.projectId;
    if (projectId) {
      queryClient.invalidateQueries({ queryKey: ['geospatial', projectId] });
    }
  });

  // =========================================================================
  // ORGANIZATION EVENTS
  // =========================================================================

  registerHandler('organization:created', (event) => {
    if (import.meta.env.DEV) devLog('Organization created:', event);
    // Invalidate all organizations list
    queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] });
  });

  registerHandler('organization:deleted', (event) => {
    if (import.meta.env.DEV) devLog('Organization deleted:', event);
    if (event.organizationName) {
      queryClient.invalidateQueries({ queryKey: ['organization', 'details', event.organizationName] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationName] });
    }
    if (event.organizationId) {
      queryClient.invalidateQueries({ queryKey: ['organization', 'byId', event.organizationId] });
    }
    queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] });
  });

  registerHandler('organization:updated', (event) => {
    if (import.meta.env.DEV) devLog('Organization updated:', event);

    // Invalidate ALL organization-related queries for comprehensive updates
    queryClient.invalidateQueries({ queryKey: ['organization'] }); // General organization queries
    queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] }); // Admin organizations list

    if (event.organizationId) {
      queryClient.invalidateQueries({ queryKey: ['organization', 'details', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId, 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'byId', event.organizationId] });
      if (import.meta.env.DEV) devLog(`Invalidated queries for organization: ${event.organizationId}`);
    }

    if (event.organizationName) {
      queryClient.invalidateQueries({ queryKey: ['organization', 'details', event.organizationName] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationName] });
    }
  });

  // Wave H4/I4: organization:renamed — name changed by admin via PUT /api/organizations/:orgId/rename
  // Invalidates org list, the specific org cache by ID, and user profiles for affected members
  // (profile may embed the org name and needs to reflect the rename).
  registerHandler('organization:renamed', (event) => {
    if (import.meta.env.DEV) devLog('Organization renamed:', event);

    // Always refresh the full org list so the sidebar reflects the new name immediately.
    queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] });

    if (event.organizationId) {
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'details', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'byId', event.organizationId] });
    }

    // Rename can change the org name embedded in user profiles — invalidate profiles broadly.
    queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
  });

  // Handle organization member events (colon format - matches backend emit format)
  registerHandler('organization:member:added', (event) => {
    if (import.meta.env.DEV) devLog('Organization member added:', event);

    // Invalidate ALL organization-related queries for comprehensive updates
    queryClient.invalidateQueries({ queryKey: ['organization'] }); // General organization queries
    queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] }); // Admin organizations list

    if (event.organizationId) {
      queryClient.invalidateQueries({ queryKey: ['organization', 'details', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId, 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'byId', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId, 'members'] });
      if (import.meta.env.DEV) devLog(`Member added - Invalidated queries for organization: ${event.organizationId}`);
    }
    if (event.organizationName) {
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationName] });
    }
  });

  registerHandler('organization:member:removed', (event) => {
    if (import.meta.env.DEV) devLog('Organization member removed:', event);

    // Invalidate ALL organization-related queries for comprehensive updates
    queryClient.invalidateQueries({ queryKey: ['organization'] }); // General organization queries
    queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] }); // Admin organizations list

    if (event.organizationId) {
      queryClient.invalidateQueries({ queryKey: ['organization', 'details', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId, 'projects'] });
      queryClient.invalidateQueries({ queryKey: ['organization', 'byId', event.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationId, 'members'] });
      if (import.meta.env.DEV) devLog(`Member removed - Invalidated queries for organization: ${event.organizationId}`);
    }
    if (event.organizationName) {
      queryClient.invalidateQueries({ queryKey: ['organization', event.organizationName] });
    }
  });

  // =========================================================================
  // DATAROOM EVENTS
  // =========================================================================

  registerHandler('dataroom:fileAdded', (event) => {
    if (import.meta.env.DEV) devLog('Dataroom file added:', event);
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['dataroom', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['dataroom', 'corpus-metadata', event.projectId] });
    }
    // The DMS dataroom list shows per-project file counts; invalidate that
    // query directly (key: ['dataroom', 'projects']).
    queryClient.invalidateQueries({ queryKey: ['dataroom', 'projects'] });
    // Older `projects` v2 list (browse/dashboard views) may also show counts.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects' && key[1] === 'v2';
      },
      refetchType: 'active',
    });
  });

  registerHandler('dataroom:fileDeleted', (event) => {
    if (import.meta.env.DEV) devLog('Dataroom file deleted:', event);
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['dataroom', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['dataroom', 'corpus-metadata', event.projectId] });
    }
    queryClient.invalidateQueries({ queryKey: ['dataroom', 'projects'] });
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects' && key[1] === 'v2';
      },
      refetchType: 'active',
    });
  });

  registerHandler('dataroom:metadataUpdated', (event) => {
    if (import.meta.env.DEV) devLog('Dataroom metadata updated:', event);
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['dataroom', 'corpus-metadata', event.projectId] });
    }
    queryClient.invalidateQueries({ queryKey: ['knowledge-repo', 'file-intelligence'] });
  });

  // =========================================================================
  // KNOWLEDGE REPO / PIPELINE COMPLETION EVENTS
  // =========================================================================
  // These events fire when DocumentMetadataPipeline finishes a batch —
  // corpus metadata rows are freshly written. Dataroom detail panels that
  // rely on that data must invalidate so the detail panel reflects the
  // latest extracted values without waiting out the staleTime.

  registerHandler('knowledge:processing-complete', (event) => {
    if (import.meta.env.DEV) devLog('Knowledge processing complete:', event);
    // The payload has org_id + fileIds but no projectId. Invalidate broadly
    // via predicate — React Query only refetches mounted queries, so the
    // cost is proportional to what's actually on screen.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== 'dataroom') return false;
        return key[1] === 'corpus-metadata';
      },
    });
    queryClient.invalidateQueries({ queryKey: ['knowledge-repo', 'file-intelligence'] });
  });

  // =========================================================================
  // NOTE EVENTS
  // =========================================================================

  registerHandler('note:created', (event) => {
    if (import.meta.env.DEV) devLog('Note created:', event);
    if (event.projectId) {
      // Invalidate notes queries
      queryClient.invalidateQueries({ queryKey: ['notes', event.projectId] });
    }
  });

  registerHandler('note:updated', (event) => {
    if (import.meta.env.DEV) devLog('Note updated:', event);
    if (event.noteId) {
      // Invalidate specific note
      queryClient.invalidateQueries({ queryKey: ['note', event.noteId] });
    }
    if (event.projectId) {
      // Invalidate notes list
      queryClient.invalidateQueries({ queryKey: ['notes', event.projectId] });
    }
  });

  registerHandler('note:deleted', (event) => {
    if (import.meta.env.DEV) devLog('Note deleted:', event);
    if (event.noteId) {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['note', event.noteId] });
    }
    if (event.projectId) {
      // Invalidate notes list
      queryClient.invalidateQueries({ queryKey: ['notes', event.projectId] });
    }
  });

  // =========================================================================
  // USER EVENTS (admin operations)
  // =========================================================================

  // NOTE: User mutations in useAdmin.ts already apply optimistic updates via userCache.
  // These handlers only need to invalidate stats (aggregates), not user lists.
  registerHandler('user:approved', (event) => {
    if (import.meta.env.DEV) devLog('User approved:', event);
    // Only invalidate stats - user lists already updated optimistically by useAdmin.ts
    // Invalidating user lists would trigger unnecessary refetches
    queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
  });

  registerHandler('user:rejected', (event) => {
    if (import.meta.env.DEV) devLog('User rejected:', event);
    // Only invalidate stats - user lists already updated optimistically by useAdmin.ts
    queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
  });

  registerHandler('user:updated', (event) => {
    if (import.meta.env.DEV) {
      devLog('User updated:', event);
      devLog('User updated - userData:', event.userData);
      devLog('User updated - userId:', event.userId);
    }

    // Skip if there's an optimistic mutation in progress
    // This prevents WebSocket updates from overwriting optimistic UI updates
    const isOptimisticMutationInProgress = queryClient.isMutating({
      mutationKey: ['update-user']
    }) > 0;

    if (isOptimisticMutationInProgress) {
      if (import.meta.env.DEV) devLog('User updated - optimistic mutation in progress, skipping WebSocket update');
      return;
    }

    // Direct cache update for instant UI update (no refetch needed)
    if (event.userId && event.userData) {
      // Update in all users list
      queryClient.setQueryData<UserCacheData[]>(['admin', 'users'], (old) => {
        if (!old) {
          if (import.meta.env.DEV) devLog('User updated - no existing cache data');
          return old;
        }

        const updatedUsers = old.map((user) => {
          if (user.uid === event.userId) {
            if (import.meta.env.DEV) devLog(`User updated - updating user ${event.userId}:`, { ...user, ...event.userData });
            return { ...user, ...event.userData };
          }
          return user;
        });

        if (import.meta.env.DEV) devLog('User updated - cache updated for admin users');
        return updatedUsers;
      });

      // Update in pending users list if status changed
      queryClient.setQueryData<UserCacheData[]>(['admin', 'users', 'pending'], (old) => {
        if (!old) return old;

        // If user status changed to non-pending, remove from pending list
        if (event.userData.status && event.userData.status !== 'pending') {
          return old.filter((user) => user.uid !== event.userId);
        }

        // Otherwise update in place
        return old.map((user) =>
          user.uid === event.userId ? { ...user, ...event.userData } : user
        );
      });

      // Update specific user cache if it exists
      queryClient.setQueryData(['user', event.userId], (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        return { ...old, ...event.userData };
      });

      // NOTE: Do NOT invalidate user lists after direct cache update - causes unnecessary refetches.
      // Only invalidate stats (aggregates) since those might need recalculation.
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });

      // Invalidate organization queries if user's organization changed
      if (event.userData.organizationId) {
        queryClient.invalidateQueries({ queryKey: ['organizations'] });
        queryClient.invalidateQueries({ queryKey: ['organizations', 'all'] });
      }

      if (import.meta.env.DEV) devLog('User updated - direct cache update applied, stats invalidated');
    } else {
      // Fallback: If no userData provided, we need to invalidate to get fresh data
      if (import.meta.env.DEV) devLog('User updated - no userData, falling back to invalidation');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'pending'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    }
  });

  registerHandler('user:deleted', (event) => {
    if (import.meta.env.DEV) devLog('User deleted:', event);

    // Direct cache removal instead of invalidation (prevents unnecessary refetch)
    if (event.userId) {
      // Remove from all users list
      queryClient.setQueryData<UserCacheData[]>(['admin', 'users'], (old) => {
        if (!old) return old;
        return old.filter((user) => user.uid !== event.userId);
      });

      // Remove from pending users list
      queryClient.setQueryData<UserCacheData[]>(['admin', 'users', 'pending'], (old) => {
        if (!old) return old;
        return old.filter((user) => user.uid !== event.userId);
      });

      // Only invalidate stats (aggregates need recalculation)
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    } else {
      // Fallback: If no userId provided, invalidate to get fresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    }
  });

  // H1: User status changed (e.g., admin toggling active/suspended)
  registerHandler('user:status:changed', (event) => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'pending-users'] });
    const userId = event?.userId;
    if (userId) {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    }
  });

  // H1: User subscription changed
  registerHandler('user:subscription:changed', (event) => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    const userId = event?.userId;
    if (userId) {
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
    }
  });

  // =========================================================================
  // NOTIFICATION EVENTS
  // =========================================================================

  registerHandler('notification:created', (event) => {
    if (import.meta.env.DEV) devLog('New notification:', event);
    // Invalidate notifications query to fetch new notification
    queryClient.invalidateQueries({ queryKey: ['user', 'notifications'] });
  });

  // =========================================================================
  // PORTFOLIO EVENTS (C2 - previously ZERO handlers)
  // Backend emits these via socketService; query keys from usePortfolioHoldings,
  // usePortfolioSummary, usePortfolioAggregations hooks
  // =========================================================================

  registerHandler('portfolio:holding:created', (event) => {
    if (import.meta.env.DEV) devLog('Portfolio holding created:', event);
    const orgId = event.orgId as string | undefined;
    if (orgId) {
      // Invalidate holdings list: ['portfolio-holdings', orgId, ...]
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings', orgId] });
      // Invalidate summary: ['portfolio-summary', orgId]
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary', orgId] });
      // Invalidate aggregations: ['portfolio-aggregation', orgId, ...]
      queryClient.invalidateQueries({ queryKey: ['portfolio-aggregation', orgId] });
      // Invalidate stage view: ['portfolio', orgId, 'by-stage']
      queryClient.invalidateQueries({ queryKey: ['portfolio', orgId, 'by-stage'] });
    }
  });

  registerHandler('portfolio:holding:updated', (event) => {
    if (import.meta.env.DEV) devLog('Portfolio holding updated:', event);
    const orgId = event.orgId as string | undefined;
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-aggregation', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio', orgId, 'by-stage'] });
    }
  });

  registerHandler('portfolio:holding:deleted', (event) => {
    if (import.meta.env.DEV) devLog('Portfolio holding deleted:', event);
    const orgId = event.orgId as string | undefined;
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-aggregation', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio', orgId, 'by-stage'] });
    }
  });

  registerHandler('portfolio:holding:extraction_complete', (event) => {
    if (import.meta.env.DEV) devLog('Portfolio extraction complete:', event);
    const orgId = event.orgId as string | undefined;
    if (orgId) {
      // Extraction updates holding data, so invalidate everything
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-aggregation', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio', orgId, 'by-stage'] });
    }
  });

  registerHandler('portfolio:aggregates:updated', (event) => {
    if (import.meta.env.DEV) devLog('Portfolio aggregates updated:', event);
    const orgId = event.orgId as string | undefined;
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-aggregation', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio', orgId, 'by-stage'] });
    }
  });

  registerHandler('portfolio:cashflow:added', (event) => {
    if (import.meta.env.DEV) devLog('Portfolio cashflow added:', event);
    const orgId = event.orgId as string | undefined;
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary', orgId] });
    }
  });

  registerHandler('portfolio:cashflow:deleted', (event) => {
    if (import.meta.env.DEV) devLog('Portfolio cashflow deleted:', event);
    const orgId = event.orgId as string | undefined;
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings', orgId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary', orgId] });
    }
  });

  // =========================================================================
  // SHARE EVENTS (C4 - centralized safety net)
  // useShareRealtime.ts handles these at the component level, but these
  // centralized handlers ensure cache is invalidated even when the share
  // components are not mounted.
  // Query keys from @/utils/queryKeys: ['shares', ...]
  // =========================================================================

  registerHandler('share:created', (event) => {
    if (import.meta.env.DEV) devLog('Share created:', event);
    queryClient.invalidateQueries({ queryKey: ['shares', 'incoming'] });
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['shares', 'project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['shares', 'history', event.projectId] });
    }
  });

  registerHandler('share:accepted', (event) => {
    if (import.meta.env.DEV) devLog('Share accepted:', event);
    queryClient.invalidateQueries({ queryKey: queryKeys.shares.all });
    queryClient.invalidateQueries({
      queryKey: ['dataroom', 'projects'],
      refetchType: 'active',
    });
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['shares', 'project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['shares', 'history', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
    }
    // Project access changed - invalidate project lists
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects';
      },
      refetchType: 'active',
    });
  });

  registerHandler('share:declined', (event) => {
    if (import.meta.env.DEV) devLog('Share declined:', event);
    queryClient.invalidateQueries({ queryKey: queryKeys.shares.all });
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['shares', 'history', event.projectId] });
    }
  });

  registerHandler('share:revoked', (event) => {
    if (import.meta.env.DEV) devLog('Share revoked:', event);
    queryClient.invalidateQueries({ queryKey: queryKeys.shares.all });
    queryClient.invalidateQueries({
      queryKey: ['dataroom', 'projects'],
      refetchType: 'active',
    });
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['shares', 'project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['shares', 'history', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
    }
    // Access revoked - invalidate project lists
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'projects';
      },
      refetchType: 'active',
    });
  });

  registerHandler('share:expired', (event) => {
    if (import.meta.env.DEV) devLog('Share expired:', event);
    queryClient.invalidateQueries({ queryKey: queryKeys.shares.all });
    queryClient.invalidateQueries({
      queryKey: ['dataroom', 'projects'],
      refetchType: 'active',
    });
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-details', event.projectId] });
    }
  });

  registerHandler('share:cancelled', (event) => {
    if (import.meta.env.DEV) devLog('Share cancelled:', event);
    queryClient.invalidateQueries({ queryKey: queryKeys.shares.all });
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['shares', 'project', event.projectId] });
      queryClient.invalidateQueries({ queryKey: ['shares', 'history', event.projectId] });
    }
  });

  // C1/L16: Access revoked - critical for immediate UI update when access is removed.
  //
  // The wire shape is a union (see `SocketEventMap['access:revoked']` in socketService.ts):
  // project-scoped kicks send `{ projectId, reason }`; the organization-membership kick
  // (`kickUserFromOrganizationRoom`) sends `{ organizationId, reason }` instead. Branching
  // on `event?.projectId` alone left the organization branch a silent no-op — a user whose
  // ORG MEMBERSHIP was revoked (as opposed to one project's share) kept every cached
  // project and dataroom list until something else happened to refetch it.
  //
  // Both branches also now invalidate the authoritative `['dataroom', 'projects']` list
  // key (see `useDataroomProjects` / the `share:*` handlers above) — the project-scoped
  // branch previously invalidated only the single project's own cache, missing the DMS
  // dataroom list that renders per-project row counts and visibility.
  registerHandler('access:revoked', (event) => {
    const projectId = event?.projectId;
    if (projectId) {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['dataroom', projectId] });
      queryClient.invalidateQueries({ queryKey: ['dataroom', 'projects'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.shares.all });
      return;
    }

    const organizationId = event?.organizationId;
    if (organizationId) {
      // An organization-membership revocation can change this user's visibility into
      // every project and dataroom their (former) organization reached — there is no
      // single project id to scope to, so the equivalent of the project branch above is
      // the full project/dataroom list surface plus the org's own cached record.
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['dataroom', 'projects'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.shares.all });
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
    }
  });

  // =========================================================================
  // DEALFLOW EVENTS (H6 - previously no centralized handlers)
  // useDealFlowSubscription handles 'dealflow:updated' and 'dealflow:stages:updated'
  // at the component level. These centralized handlers cover config changes
  // (card fields, field options, deal fields) and provide a safety net.
  // Query keys from useDealFlow hooks: ['dealflow', ...]
  // =========================================================================

  // H2: Centralized dealflow:updated handler (was in useDealFlow.ts)
  registerHandler('dealflow:updated', (event) => {
    if (event?.dealId && isRecentMutation(event.dealId)) {
      if (import.meta.env.DEV) devLog('Skipping dealflow:updated - recent local mutation for:', event.dealId);
      return;
    }
    const orgId = event?.organization;
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: ['dealflow', 'organization', orgId] });
    }
    queryClient.invalidateQueries({ queryKey: ['dealflow', 'user'] });
  });

  registerHandler('dealflow:stages:updated', (_event) => {
    if (import.meta.env.DEV) devLog('Dealflow stages updated:', _event);
    // Invalidate all dealflow queries - stages affect everything
    queryClient.invalidateQueries({ queryKey: ['dealflow'] });
  });

  registerHandler('dealflow:cardfields:updated', (_event) => {
    if (import.meta.env.DEV) devLog('Dealflow card fields updated:', _event);
    queryClient.invalidateQueries({ queryKey: ['dealflow', 'cardFields'] });
    // Card fields affect how deals are displayed
    queryClient.invalidateQueries({ queryKey: ['dealflow', 'user'] });
    queryClient.invalidateQueries({ queryKey: ['dealflow', 'organization'] });
  });

  // H5: Dealflow field options updated - invalidate all dealflow queries
  registerHandler('dealflow:fieldoptions:updated', (_event) => {
    if (import.meta.env.DEV) devLog('Dealflow field options updated:', _event);
    queryClient.invalidateQueries({ queryKey: ['dealflow'] });
  });

  registerHandler('dealflow:dealfields:updated', (event) => {
    if (import.meta.env.DEV) devLog('Dealflow deal fields updated:', event);
    // Individual deal field values changed - refresh deal lists
    queryClient.invalidateQueries({ queryKey: ['dealflow', 'user'] });
    queryClient.invalidateQueries({ queryKey: ['dealflow', 'organization'] });
  });

  // H4: Dealflow priority updated
  registerHandler('dealflow:priority:updated', (event) => {
    if (event?.dealId && isRecentMutation(event.dealId)) {
      if (import.meta.env.DEV) devLog('Skipping dealflow:priority:updated - recent local mutation for:', event.dealId);
      return;
    }
    const orgId = event?.organization;
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: ['dealflow', 'organization', orgId] });
    }
    queryClient.invalidateQueries({ queryKey: ['dealflow', 'user'] });
  });

  // =========================================================================
  // QUANT COMPLETION EVENTS
  // =========================================================================

  registerHandler('quant:run-done', (event) => {
    const projectId = typeof event?.projectId === 'string' && event.projectId.trim() !== ''
      ? event.projectId
      : null;
    if (!projectId) return;

    quantProjectCompletionInvalidationTargets(projectId).forEach(({ queryKey }) => {
      debounceQueryInvalidation(queryKey);
    });

    const jobId = typeof event.jobId === 'string' && event.jobId.trim() !== '' ? event.jobId : null;
    if (jobId) {
      quantJobCompletionInvalidationTargets(jobId).forEach(({ queryKey }) => {
        debounceQueryInvalidation(queryKey);
      });
    }
  });

  registerHandler('quant:job-done', (event) => {
    const jobId = typeof event?.jobId === 'string' && event.jobId.trim() !== '' ? event.jobId : null;
    const projectId = typeof event?.projectId === 'string' && event.projectId.trim() !== ''
      ? event.projectId
      : null;

    if (projectId) {
      quantProjectCompletionInvalidationTargets(projectId).forEach(({ queryKey }) => {
        debounceQueryInvalidation(queryKey);
      });
    }
    if (jobId) {
      quantJobCompletionInvalidationTargets(jobId).forEach(({ queryKey }) => {
        debounceQueryInvalidation(queryKey);
      });
    }
  });

  // =========================================================================
  // RISK MONITOR EVENTS
  // =========================================================================

  registerHandler('risk-monitor:updated', (event) => {
    if (import.meta.env.DEV) devLog('Risk monitor updated:', event);
    if (event.projectId) {
      queryClient.invalidateQueries({ queryKey: ['risk-monitor', 'detail', event.projectId] });
    }
    queryClient.invalidateQueries({ queryKey: ['risk-monitor', 'fleet'] });
  });

  // =========================================================================
  // CONNECTION STATUS EVENTS
  // =========================================================================

  // FIX: Removed socket:connected handler - initial data is fetched by components on mount
  // Only handle reconnection to catch up on missed updates

  registerHandler('socket:disconnected', () => {
    if (import.meta.env.DEV) devLog('WebSocket disconnected, real-time updates paused');
  });

  // FIX C5: socket:reconnected was invalidating wrong key ['notifications'] instead of ['user', 'notifications']
  registerHandler('socket:reconnected', () => {
    if (import.meta.env.DEV) devLog('WebSocket reconnected, checking for missed updates');

    // Notifications (FIX C5: correct key)
    queryClient.invalidateQueries({ queryKey: ['user', 'notifications'] });

    // Pending projects for admins only if admin view is active
    queryClient.invalidateQueries({ queryKey: ['projects', 'pending'] });

    // Invalidate organization data that might have changed
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'organization';
      }
    });

    // Invalidate active portfolio queries (data could be stale from disconnect)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && (
          key[0] === 'portfolio-holdings' ||
          key[0] === 'portfolio-summary' ||
          key[0] === 'portfolio-aggregation' ||
          (key[0] === 'portfolio' && typeof key[1] === 'string')
        );
      },
      refetchType: 'active',
    });

    // Invalidate active dealflow queries
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'dealflow';
      },
      refetchType: 'active',
    });

    // Invalidate active share queries
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'shares';
      },
      refetchType: 'active',
    });

    // Share accept/revoke/expiry events can be missed while the socket is
    // disconnected. The server project list is the authorization authority,
    // so actively reconcile it before continuing to display project metadata.
    queryClient.invalidateQueries({
      queryKey: ['dataroom', 'projects'],
      refetchType: 'active',
    });

    // Invalidate active risk monitor queries on reconnect
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'risk-monitor';
      },
      refetchType: 'active',
    });
  });

  // =========================================================================
  // STATE RECONCILIATION
  // =========================================================================

  registerHandler('state:reconciled', (data) => {
    if (import.meta.env.DEV) devLog('State reconciled, applying updates:', data);

    if (data.updates) {
      // Apply project updates
      if (data.updates.projects) {
        data.updates.projects.forEach((project) => {
          queryClient.setQueryData(['project', project.id], project);
        });
        // Invalidate project lists to show updated data
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            return Array.isArray(key) && key[0] === 'projects';
          }
        });
      }

      // Apply organization updates
      if (data.updates.organizations) {
        data.updates.organizations.forEach((org) => {
          // Use orgId (UUID) as primary cache key, fall back to name
          const orgKey = org.orgId || org.organizationName;
          queryClient.setQueryData(['organization', orgKey], org);
          if (org.orgId) {
            queryClient.setQueryData(['organization', 'byId', org.orgId], org);
          }
        });
      }

      // Apply notification updates
      if (data.updates.notifications) {
        queryClient.setQueryData(['user', 'notifications'], data.updates.notifications);
      }
    }
  });

  // Return cleanup function
  return () => {
    if (import.meta.env.DEV) devLog('[QueryClient] Cleaning up real-time invalidation handlers');
    eventHandlers.forEach(({ event, handler }) => {
      socketService.off(event, handler);
    });
  };
};

// ---------------------------------------------------------------------------
// Phase 3 task 3.7: SOCKET_EVENT_INVALIDATION_MAP
// ---------------------------------------------------------------------------
//
// Declarative mapping from canonical socket events (see
// `docs/socket-events.md` §2) → React Query keys to invalidate. Every
// event that the backend emits MUST appear in this map, even if the
// invalidation list is empty — that's how we prove, in code, that the
// event is handled deliberately rather than forgotten.
//
// An entry is a function that accepts the normalised envelope
// (event name + version + data) and returns a list of query keys
// to invalidate. Functions keep the mapping flexible — some events
// invalidate by a predicate, some by a concrete key set, some invalidate
// nothing at all (pure connection-state signals).

export type SocketInvalidationAction =
  | { type: 'invalidate'; queryKey: QueryKey; refetchType?: 'active' | 'all' | 'none' }
  | { type: 'remove'; queryKey: QueryKey }
  | { type: 'predicate'; predicate: (key: QueryKey) => boolean; refetchType?: 'active' | 'all' | 'none' };

export type SocketInvalidationResolver = (
  envelope: NormalizedSocketEnvelope,
) => SocketInvalidationAction[];

export const SOCKET_EVENT_INVALIDATION_MAP: Record<string, SocketInvalidationResolver> = {
  // -------------------- Phase 3 task 3.6: stale-drop hint --------------------
  'file:status-updated': (env) => {
    const orgId = env.org_id;
    return [
      {
        type: 'predicate',
        predicate: (key) =>
          Array.isArray(key) &&
          key[0] === 'dataroom' &&
          key[1] === 'corpus-metadata',
      },
      ...(orgId
        ? [
            {
              type: 'invalidate' as const,
              queryKey: ['dataroom', 'corpus-metadata', orgId],
            },
          ]
        : []),
    ];
  },

  // -------------------- Knowledge repo / pipeline ---------------------------
  'knowledge:processing-complete': () => [
    { type: 'invalidate', queryKey: ['knowledge-repo', 'knowledge-base'] },
    { type: 'invalidate', queryKey: ['knowledge-repo', 'file-intelligence'] },
    {
      type: 'predicate',
      predicate: (key) =>
        Array.isArray(key) &&
        key[0] === 'dataroom' &&
        key[1] === 'corpus-metadata',
    },
  ],
  'knowledge:file-status-updated': () => [
    { type: 'invalidate', queryKey: ['knowledge-repo', 'knowledge-base'] },
    { type: 'invalidate', queryKey: ['knowledge-repo', 'file-intelligence'] },
    {
      type: 'predicate',
      predicate: (key) =>
        Array.isArray(key) &&
        key[0] === 'dataroom' &&
        key[1] === 'corpus-metadata',
    },
  ],

  // -------------------- Projects --------------------------------------------
  'project:created': () => [
    {
      type: 'predicate',
      predicate: (key) => Array.isArray(key) && key[0] === 'projects',
    },
  ],
  'project:updated': (env) => {
    const id =
      (env.data.id as string | undefined) ?? (env.data.projectId as string | undefined);
    return [
      ...(id
        ? [
            { type: 'invalidate' as const, queryKey: ['project', id] },
            { type: 'invalidate' as const, queryKey: ['project-details', id] },
            { type: 'invalidate' as const, queryKey: ['project-capital-stack', id] },
          ]
        : []),
      {
        type: 'predicate',
        predicate: (key) =>
          Array.isArray(key) && key[0] === 'projects' && key[1] === 'v2',
        refetchType: 'active',
      },
    ];
  },
  'project:deleted': (env) => {
    const id = env.data.projectId as string | undefined;
    return id
      ? [
          { type: 'remove', queryKey: ['project', id] },
          { type: 'remove', queryKey: ['project-details', id] },
          { type: 'remove', queryKey: ['project-capital-stack', id] },
          {
            type: 'predicate',
            predicate: (key) => Array.isArray(key) && key[0] === 'projects',
          },
        ]
      : [];
  },

  // -------------------- Dataroom --------------------------------------------
  'dataroom:fileAdded': (env) => {
    const projectId = env.data.projectId as string | undefined;
    return projectId
      ? [
          { type: 'invalidate', queryKey: ['dataroom', projectId] },
          { type: 'invalidate', queryKey: ['dataroom', 'corpus-metadata', projectId] },
          // DMS list shows per-project file counts; without this, the count
          // stays stale until React Query staleTime expires.
          { type: 'invalidate', queryKey: ['dataroom', 'projects'] },
        ]
      : [];
  },
  'dataroom:fileDeleted': (env) => {
    const projectId = env.data.projectId as string | undefined;
    return projectId
      ? [
          { type: 'invalidate', queryKey: ['dataroom', projectId] },
          { type: 'invalidate', queryKey: ['dataroom', 'corpus-metadata', projectId] },
          { type: 'invalidate', queryKey: ['dataroom', 'projects'] },
        ]
      : [];
  },
  'dataroom:metadataUpdated': (env) => {
    const projectId = env.data.projectId as string | undefined;
    return projectId
      ? [
          { type: 'invalidate', queryKey: ['dataroom', 'corpus-metadata', projectId] },
          { type: 'invalidate', queryKey: ['knowledge-repo', 'file-intelligence'] },
        ]
      : [];
  },

  // -------------------- Pure-signal events (no cache action) ----------------
  'socket:connected': () => [],
  'socket:disconnected': () => [],
  'socket:reconnected': () => [],
};

/**
 * Apply a list of invalidation actions to a QueryClient.
 */
function applyInvalidationActions(
  client: QueryClient,
  actions: SocketInvalidationAction[],
): void {
  for (const action of actions) {
    switch (action.type) {
      case 'invalidate':
        client.invalidateQueries({
          queryKey: action.queryKey,
          ...(action.refetchType ? { refetchType: action.refetchType } : {}),
        });
        break;
      case 'remove':
        client.removeQueries({ queryKey: action.queryKey });
        break;
      case 'predicate':
        client.invalidateQueries({
          predicate: (query) => action.predicate(query.queryKey),
          ...(action.refetchType ? { refetchType: action.refetchType } : {}),
        });
        break;
    }
  }
}

/**
 * Minimal socket-like shape accepted by `registerSocketInvalidation`.
 * Decouples the registrar from the concrete socket.io client, letting
 * the unit tests inject a fake emitter.
 */
export interface SocketLikeEmitter {
  on(event: string, handler: (payload: unknown) => void): void;
  off(event: string, handler: (payload: unknown) => void): void;
}

/**
 * Register handlers for every event in `SOCKET_EVENT_INVALIDATION_MAP`.
 * Returns a cleanup function that detaches every listener.
 *
 * Version semantics:
 *   - v <= CURRENT_CLIENT_VERSION → invoke the mapped resolver.
 *   - v  > CURRENT_CLIENT_VERSION → log warn + run the resolver anyway
 *     (soft-refresh the affected slice) so we stay eventually consistent
 *     even when the backend rolls forward first. See
 *     `docs/socket-events.md` §1 for the rationale.
 */
export function registerSocketInvalidation(
  client: QueryClient,
  socket: SocketLikeEmitter,
  map: Record<string, SocketInvalidationResolver> = SOCKET_EVENT_INVALIDATION_MAP,
): () => void {
  const handlers: Array<{ event: string; handler: (raw: unknown) => void }> = [];

  for (const [eventName, resolver] of Object.entries(map)) {
    const handler = (raw: unknown): void => {
      const envelope = normalizeSocketPayload(eventName, raw);

      if (envelope.version > CURRENT_CLIENT_VERSION) {
        // Forward-compat: warn but still refresh so the UI stays in sync.
        console.warn('[socket] unknown event version — soft refreshing', {
          event: eventName,
          version: envelope.version,
          clientVersion: CURRENT_CLIENT_VERSION,
        });
      }

      try {
        const actions = resolver(envelope);
        applyInvalidationActions(client, actions);
      } catch (err) {
        // A buggy resolver must never crash the socket pipe.
        console.error('[socket] resolver threw', {
          event: eventName,
          err,
        });
      }
    };
    socket.on(eventName, handler);
    handlers.push({ event: eventName, handler });
  }

  return () => {
    for (const { event, handler } of handlers) {
      socket.off(event, handler);
    }
  };
}

// Helper function to prefetch data
export const prefetchQuery = async <T = unknown>(
  queryKey: QueryKey,
  queryFn: () => Promise<T>,
  staleTime?: number
) => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: staleTime ?? 1000 * 60 * 2 // Default 2 minutes
  });
};

// Helper to optimistically update cache
export const optimisticUpdate = <T>(
  queryKey: QueryKey,
  updater: (oldData: T | undefined) => T
) => {
  queryClient.setQueryData(queryKey, updater);
};

// Helper to manually invalidate queries
export const invalidateQueries = (queryKey?: QueryKey) => {
  if (queryKey) {
    queryClient.invalidateQueries({ queryKey });
  } else {
    queryClient.invalidateQueries();
  }
};

// Helper to remove queries from cache
export const removeQueries = (queryKey: QueryKey) => {
  queryClient.removeQueries({ queryKey });
};

// Export query client instance
export default queryClient;
